import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  ButtonCore,
  Icon,
  Loader,
  MobileDrawerItem,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import Lottie from 'lottie-react';
import PropTypes from 'prop-types';

import ActionButtonLoader from '../../../ECMv6/assets/images/lotties/actionbutton-loader.json';
import {
  NODE_DATA_MODEL,
  REASSIGN_CONFIG_MAP,
} from '../../../models/CertsModels';
import { setIsModalOpen } from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_APPLICATION_OWNER,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  PERCENTAGE_100,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
  SYSTEM_DATA_THEMES,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import { CERT_TILE_TYPE } from '../../CertsDetailsList/Tile/constant';
import {
  getCertKind,
  getEOCertKind,
  getEOFixedColKey,
  getFixedColKey,
} from '../../CertsDetailsList/Tile/utils';
import {
  EO_ACC_CERT_ENTITLEMENT_VALUEKEY,
  EO_CHILD_CERT_ENTITLEMENT_VALUEKEY,
  EO_ENT_CERT_ENTITLEMENT_VALUEKEY,
  TAB_STATUS_DONE,
  TAB_STATUS_TODO,
} from '../../ViewControls';
import { LINE_ITEM_TYPES } from '../CertsActionButtonsGroup/constants';
import CertsActionPopover from '../CertsActionPopover/CertsActionPopover';
import CertsConsultedActionPopover from '../CertsActionPopover/CertsConsultedActionPopover';
import CertsConsultPopover from '../CertsActionPopover/CertsConsultPopover';
import CertsReassignPopover from '../CertsActionPopover/CertsReassignPopover';
import CertsChangeEndDate from '../CertsChangeEndDate';
import { CertsConsultModal, CertsConsultWarnModal } from '../CertsConsultModal';
import CertsDecommissionAction from '../CertsDecommissionAction';
import { CertsReassignModal } from '../CertsReassignModal';
import CertsRejectAction from '../CertsRejectAction';
import CertsSaviActionPopover from '../CertsSaviActionPopover';
import CertsTerminateModal from '../CertsTerminateModal';
import {
  ACTION_BUTTON_GROUP_KINDS,
  ACTION_BUTTON_KINDS,
  ACTION_ICON_KINDS,
  CERT_ENTITLEMENT_VALUEKEY,
  CHECKBOX_CONSTANTS,
} from '../constant';

import { invertedMessages } from './messages';
import {
  enableReassignPopover,
  getBtnText,
  getWarnModalText,
  getWarnModalTitle,
} from './utils';

import './CertsActionButton.css';

const defaultTooltipProps = {
  text: '',
  maxWidth: '12rem',
  placement: 'bottom',
};

const CertsActionButton = forwardRef(
  (
    {
      id,
      setActionPopoverOpenId,
      setIsSidePanelCommentDrawerOpen,
      kind,
      setDrawerActionSelected,
      groupKind,
      tabIndex,
      isDisabled,
      isSelected,
      isLoading,
      isDecisionMade,
      isDrawerActions,
      isCommentRequired,
      isHidden,
      onClick,
      isToolTipDisabled,
      tooltipProperties,
      dataTheme,
      dataTestId,
      certKey,
      nodeData,
      //  TODO: Commented out at Umeshes request, add or remove based on user feedback
      // verifyParentMap,
      getViewApiConfigmap,
      extendEndDateDaysLimit,
      isActionSuccess,
      isActionError,
      getTypeActEntRole,
      isSidePanelVisible,
      reassignConfigMap,
      getFirstTimeReject,
      certificationStartDate,
      isActionErrorMessage,
      conditionalCertifyDaysLimit,
      isPopoverUserReassignMsgShown,
      setReassignUserPopoverMsg,
      setDrawerActionKind,
      isSaviResponded,
      saviRespondedReason,
      isSidePanelCommentDrawerOpen,
      consulteeLoggedIn,
      actionTakenByPrimaryCertifier,
      isScrolling,
      globalDateFormat,
      isActionGroupExpanded,
      activeTab,
      filteredNodeData,
      isActionDrawerOutsideClick,
      setIsActionDrawerOutsideClick,
    },
    ref
  ) => {
    const { campaignType } = useSelector(
      (state) => state?.certifications?.details?.core
    );
    const isEOCampaign =
      CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
    const isAOCampaign =
      CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_APPLICATION_OWNER;
    const getCertIconKinds = isEOCampaign
      ? getEOCertKind(nodeData)
      : getCertKind(nodeData);
    const getCertColKeys = isEOCampaign
      ? getEOFixedColKey(getCertIconKinds)
      : getFixedColKey(getCertIconKinds);
    const isNoValue =
      filteredNodeData[getCertColKeys] === null ||
      filteredNodeData[getCertColKeys] === undefined ||
      filteredNodeData?.[getCertColKeys]?.trim()?.length === 0;
    const certTitleColValue = isNoValue
      ? 'Not Provided'
      : filteredNodeData[getCertColKeys];
    // Modals and pop ups
    const [isCommentPopoverOpen, setIsCommentPopoverOpen] = useState(false);
    // const [isProceedStatus, setIsProceedStatus] = useState(false);
    // const [isActionAlertModalOpen, setIsActionAlertModalOpen] = useState(false);
    const [isConsultPopoverShown, setIsConsultPopoverShown] = useState(false);
    const [isConsultWarnShown, setIsConsultWarnShown] = useState(false);
    const [isConsultWarnModalOpen, setIsConsultWarnModalOpen] = useState(false);
    const [isRejectPopoverOpen, setIsRejectPopoverOpen] = useState(false);
    const [isDecommissionPopoverOpen, setIsDecommissionPopoverOpen] =
      useState(false);
    const [checkTerminateClick, setIsCheckTerminateClick] = useState(false);
    const [isTerminateError, setIsTerminateError] = useState(false);
    const [isExtendEndDateClick, setIsExtendEndDateClick] = useState(false);
    const [isExtendEndDateError, setIsExtendEndDateError] = useState(false);
    const [isConditionalCertifyClick, setIsConditionalCertifyClick] =
      useState(false);
    const [getIsConditionalCertifyError, setIsConditionalCertifyError] =
      useState(false);
    const [getCurrentDate, setCurrentDate] = useState(false);
    const getDefaultDate =
      nodeData?.ENDDATE &&
      nodeData?.ENDDATE?.length > 0 &&
      new Date(nodeData?.ENDDATE);
    const [getIsRejectError, setIsRejectError] = useState(false);
    const [isSaviApprovePopoverOpen, setIsSaviApprovePopoverOpen] =
      useState(false);
    const [isSaviRejectPopoverOpen, setIsSaviRejectPopoverOpen] =
      useState(false);
    const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
    const [reassignModalDetails, setReassignModalDetails] = useState(null);
    const [isConsultModalOpen, setIsConsultModalOpen] = useState(false);
    const [consultModalDetails, setconsultModalDetails] = useState(null);
    const dispatch = useDispatch();

    const { isActionModalOpen } = useSelector(
      (state) => state?.certifications?.details?.lineActions
    );
    const { mediaQueryValue } = useSelector(
      (state) => state?.certifications?.details?.global
    );
    const { messages } = useSelector((state) => state?.user);
    const l = useInvertedLocalize(invertedMessages);
    const intl = useIntl();

    const consultPopoverFlag = localStorage.getItem(
      CHECKBOX_CONSTANTS.CONSULT_POPOVER
    );
    const consultWarnFlag = localStorage.getItem(
      CHECKBOX_CONSTANTS.CONSULT_WARN
    );

    const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
      (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
    );
    const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

    const APPROVE_TEXT =
      messages['Approve.Default.label.ATTESTATIONS'] ||
      localize(intl, getMessageKey('Approve.Default.label.ATTESTATIONS'));

    const APPROVED_TEXT =
      messages['REQUEST.STATUS.APPROVED'] ||
      localize(intl, getMessageKey('REQUEST.STATUS.APPROVED'));

    const CONFIRM_MANAGER_TEXT =
      messages['Confirmmanager.label.ATTESTATIONS'] ||
      localize(intl, getMessageKey('Confirmmanager.label.ATTESTATIONS'));

    const MANAGER_CONFIRMED_TEXT =
      messages['ManagerConfirmed.ATTESTATIONS'] ||
      localize(intl, getMessageKey('ManagerConfirmed.ATTESTATIONS'));

    useEffect(() => {
      // changing the current date format from yyyy-mm-dd to mm/dd/yyyy format
      if (nodeData?.User_ENDDATE && nodeData?.User_ENDDATE?.length > 0) {
        let getDate = nodeData?.User_ENDDATE?.split(' ')[0]?.split('-');

        getDate = `${getDate[1]}/${getDate[2]}/${getDate[0]}`;
        setCurrentDate(getDate);
      } else {
        setCurrentDate('-');
      }
    }, []);

    useEffect(() => {
      if (isScrolling) {
        if (
          isCommentPopoverOpen ||
          isRejectPopoverOpen ||
          isDecommissionPopoverOpen
        ) {
          setIsRejectPopoverOpen(false);
          setIsCommentPopoverOpen(false);
          setActionPopoverOpenId(false);
          setIsDecommissionPopoverOpen(false);
        }
      }
    }, [isScrolling]);

    useEffect(() => {
      if (
        isCommentPopoverOpen ||
        isRejectPopoverOpen ||
        isDecommissionPopoverOpen
      ) {
        setIsRejectPopoverOpen(false);
        setIsCommentPopoverOpen(false);
        setActionPopoverOpenId(false);
        setIsDecommissionPopoverOpen(false);
      }
    }, [isActionGroupExpanded]);

    // Set to IsModalOpen when a modal is open
    useEffect(() => {
      const isModalOpen =
        isReassignModalOpen ||
        isConsultModalOpen ||
        isConsultWarnModalOpen ||
        isExtendEndDateClick ||
        checkTerminateClick ||
        isConditionalCertifyClick;

      if (isModalOpen) dispatch(setIsModalOpen(isModalOpen));
      if (isActionModalOpen && !isModalOpen)
        dispatch(setIsModalOpen(isModalOpen));
    }, [
      isReassignModalOpen,
      isConsultModalOpen,
      isConsultWarnModalOpen,
      // TODO: Request these renamed to isXxxModalOpen
      isExtendEndDateClick,
      checkTerminateClick,
      isConditionalCertifyClick,
    ]);

    const {
      APPROVE,
      REJECT,
      TERMINATE,
      REASSIGN,
      CONSULT,
      WORKS_FOR_ME,
      DOES_NOT_WORK_FOR_ME,
      EXTEND_END_DATE,
      CONDITIONAL_CERTIFIY,
      BELONGS_TO_ME,
      DOES_NOT_BELONG_TO_ME,
      DECOMMISSION,
      EDIT,
    } = ACTION_BUTTON_KINDS;

    const isDisabledException = () => {
      switch (kind) {
        case REASSIGN:
        case WORKS_FOR_ME:
        case DOES_NOT_WORK_FOR_ME:
          return true;
        default:
          return false;
      }
    };

    const isFooterGroup = groupKind === ACTION_BUTTON_GROUP_KINDS.FOOTER;
    const isDetailsGroup =
      groupKind === ACTION_BUTTON_GROUP_KINDS.TILE &&
      kind !== TERMINATE && // Eliminating Terminate Action for Modal creation
      kind !== EXTEND_END_DATE && // Eliminating Extend End Date Action for Modal creation
      kind !== REJECT && // Eliminating Reject Action for Popover creation
      kind !== DECOMMISSION && // Eliminating Decommission Action for Popover creation
      kind !== DOES_NOT_WORK_FOR_ME && // Eliminating Reassign action for user
      kind !== DOES_NOT_BELONG_TO_ME && // // Eliminating Reassign action for Account in SA campaign
      kind !== REASSIGN && // Eliminating Reassign action for Account, Role, Entitilement
      kind !== CONDITIONAL_CERTIFIY; // Eliminating Conditional Certify Action for Modal creation

    const isConfirmManagerOrApproveTileAndCommentRequired =
      (kind === WORKS_FOR_ME ||
        kind === BELONGS_TO_ME ||
        (kind === APPROVE && groupKind === ACTION_BUTTON_GROUP_KINDS.TILE)) &&
      isCommentRequired;
    const isReassignUserOrParent =
      kind === DOES_NOT_WORK_FOR_ME ||
      kind === DOES_NOT_BELONG_TO_ME ||
      (isAOCampaign &&
        getTypeActEntRole === CERT_TILE_TYPE.ACCOUNT &&
        kind === REASSIGN);
    const isConsult = kind === CONSULT;
    const { isConsulted } = nodeData;

    // Needs to set false on is footer render when switching sidepanels
    useEffect(() => {
      if (isFooterGroup) setIsSidePanelCommentDrawerOpen(false);
    }, []);

    const actionButtonTooltipClasses = cx(
      'CertsActionButton-tooltip',
      isHidden && 'CertsActionButton-tooltip--isHidden',
      isToolTipDisabled && 'CertsActionButton-tooltip--shouldNotRender'
    );

    const actionButtonClasses = cx(
      'CertsActionButton',
      `CertsActionButton-${kind}`,
      isDisabledException() && 'CertsActionButton--isDisabledException',
      isDisabled && 'CertsActionButton--isDisabled',
      isSelected && !isConsult && 'CertsActionButton--isSelected',
      isLoading && 'CertsActionButton--isLoading',
      isDecisionMade && 'CertsActionButton--isDecisionMade',
      isHidden && 'CertsActionButton--isHidden',
      isSaviResponded && 'Tile--DashedBorder',
      !actionTakenByPrimaryCertifier && !isSaviResponded && 'Tile--DashedBorder'
    );

    // Merge provided tooltipProperties with defaults
    const mergedTooltipProperties = {
      ...defaultTooltipProps,
      ...tooltipProperties,
    };

    const getPopoverPlacement = () => {
      if (groupKind === ACTION_BUTTON_GROUP_KINDS.FOOTER)
        return isSaviResponded ? 'top-start' : 'top';

      return 'left';
    };

    const getIconKind = () => {
      switch (kind) {
        case APPROVE:
          return ACTION_ICON_KINDS.CHECK;
        case BELONGS_TO_ME:
          return ACTION_ICON_KINDS.CHECK;
        case REJECT:
        case DECOMMISSION:
          return ACTION_ICON_KINDS.REJECT;
        case TERMINATE:
          return ACTION_ICON_KINDS.TERMINATE;
        case DOES_NOT_WORK_FOR_ME:
          return ACTION_ICON_KINDS.REASSIGN_USER;
        case DOES_NOT_BELONG_TO_ME:
          return ACTION_ICON_KINDS.DOES_NOT_BELONG_TO_ME;
        case REASSIGN:
          return ACTION_ICON_KINDS.REASSIGN_ITEM;
        case WORKS_FOR_ME:
          return ACTION_ICON_KINDS.CONFIRM_MANAGER;
        case EXTEND_END_DATE:
        case CONDITIONAL_CERTIFIY:
          return ACTION_ICON_KINDS.EXTEND_END_DATE;
        case CONSULT:
          return ACTION_ICON_KINDS.CONSULT;
        case EDIT:
          return ACTION_ICON_KINDS.EDIT;
        default:
          return ACTION_ICON_KINDS.CHECK;
      }
    };

    const getDrawerItemLabel = () => {
      const checkMobileSidePanelFooter =
        isMobileXlargeWidth && isFooterGroup && !isDrawerActions;

      switch (kind) {
        case APPROVE:
          return isSelected ? APPROVED_TEXT : APPROVE_TEXT;
        case REJECT:
          return 'Reject';
        case DECOMMISSION:
          return 'Decommission';
        case TERMINATE:
          return 'Terminate';
        case REASSIGN:
          return 'Reassign';
        case CONSULT:
          return checkMobileSidePanelFooter ? 'Consult' : 'Consult with Peers';
        case WORKS_FOR_ME:
          return isSelected ? MANAGER_CONFIRMED_TEXT : CONFIRM_MANAGER_TEXT;
        case DOES_NOT_WORK_FOR_ME:
        case DOES_NOT_BELONG_TO_ME:
          return 'Reassign';

        case EXTEND_END_DATE:
          return checkMobileSidePanelFooter ? 'Change Date' : 'Change End Date';
        case CONDITIONAL_CERTIFIY:
          return checkMobileSidePanelFooter
            ? 'Change Date...'
            : 'Conditional Certify';
        default:
          return null;
      }
    };

    // Update the to let tile know to increase the z-index
    useEffect(() => {
      const isConsultedWithSelectionCheck =
        !isConsult && isConsulted && kind !== EXTEND_END_DATE;
      const isReassignOrReassignUser =
        kind === REASSIGN ||
        kind === DOES_NOT_WORK_FOR_ME ||
        kind === DOES_NOT_BELONG_TO_ME;
      const isRejectCancelingConsulted =
        isSelected &&
        groupKind === ACTION_BUTTON_GROUP_KINDS.TILE &&
        kind === REJECT;
      const isDecommissionCancelingConsulted =
        isSelected &&
        groupKind === ACTION_BUTTON_GROUP_KINDS.TILE &&
        kind === DECOMMISSION;
      const isExtendCancelingConsulted =
        isSelected &&
        groupKind === ACTION_BUTTON_GROUP_KINDS.TILE &&
        kind === EXTEND_END_DATE;
      const isTerminatedConsulted =
        isSelected &&
        groupKind === ACTION_BUTTON_GROUP_KINDS.TILE &&
        kind === TERMINATE;

      if (
        isConsultedWithSelectionCheck ||
        isReassignOrReassignUser ||
        isDetailsGroup ||
        isRejectCancelingConsulted ||
        isDecommissionCancelingConsulted ||
        isExtendCancelingConsulted ||
        isTerminatedConsulted
      ) {
        if (isCommentPopoverOpen) {
          setActionPopoverOpenId(nodeData?.tracker?.id);
        } else {
          setActionPopoverOpenId(false);
        }
      }
    }, [isCommentPopoverOpen]);

    // Re-enable outside click for tile Action drawer
    useEffect(() => {
      if (!isActionDrawerOutsideClick && !isCommentPopoverOpen)
        setIsActionDrawerOutsideClick(true);
    }, [isCommentPopoverOpen]);

    useEffect(() => {
      if (
        !isActionDrawerOutsideClick &&
        (!isRejectPopoverOpen || !isDecommissionPopoverOpen)
      )
        setIsActionDrawerOutsideClick(true);
    }, [isRejectPopoverOpen, isDecommissionPopoverOpen]);

    useEffect(() => {
      if (!isActionDrawerOutsideClick && !isReassignModalOpen)
        setIsActionDrawerOutsideClick(true);
    }, [isReassignModalOpen]);

    useEffect(() => {
      if (isConsultWarnModalOpen) setIsActionDrawerOutsideClick(false);
    }, [isConsultWarnModalOpen]);

    useEffect(() => {
      if (consultWarnFlag !== null) {
        setIsConsultWarnShown(consultWarnFlag !== 'true');
      } else if (isSidePanelVisible && isConsulted) {
        // Sidepanel open state and  taken any action  and refreshed the sidepanel data -- resetting state values
        setIsConsultWarnShown(false);
      }

      if (consultPopoverFlag !== null) {
        setIsConsultPopoverShown(consultPopoverFlag !== 'true');
      } else if (isSidePanelVisible && isConsulted) {
        // Sidepanel open state and  taken any action  and refreshed the sidepanel data -- resetting state values
        setIsConsultPopoverShown(false);
      }
    }, [consultPopoverFlag, consultWarnFlag, nodeData]);

    useEffect(() => {
      if (groupKind === ACTION_BUTTON_GROUP_KINDS.TILE && kind === REJECT) {
        if (isRejectPopoverOpen) setActionPopoverOpenId(nodeData?.tracker?.id);
        if (!isRejectPopoverOpen) setActionPopoverOpenId(false);
      }

      if (
        groupKind === ACTION_BUTTON_GROUP_KINDS.TILE &&
        kind === DECOMMISSION
      ) {
        if (isDecommissionPopoverOpen)
          setActionPopoverOpenId(nodeData?.tracker?.id);
        if (!isDecommissionPopoverOpen) setActionPopoverOpenId(false);
      }
    }, [isRejectPopoverOpen, isDecommissionPopoverOpen]);

    useEffect(() => {
      if (
        groupKind === ACTION_BUTTON_GROUP_KINDS.TILE &&
        kind === APPROVE &&
        isSaviResponded
      ) {
        if (isSaviApprovePopoverOpen)
          setActionPopoverOpenId(nodeData?.tracker?.id);
        if (!isSaviApprovePopoverOpen) setActionPopoverOpenId(false);
      }
    }, [isSaviApprovePopoverOpen]);

    useEffect(() => {
      if (
        groupKind === ACTION_BUTTON_GROUP_KINDS.TILE &&
        kind === REJECT &&
        isSaviResponded
      ) {
        if (isSaviRejectPopoverOpen)
          setActionPopoverOpenId(nodeData?.tracker?.id);
        if (!isSaviRejectPopoverOpen) setActionPopoverOpenId(false);
      }
    }, [isSaviRejectPopoverOpen]);

    const getBorderIcon = () => {
      if (isSelected && isDecisionMade)
        return 'CertsActionBtnSelectedBorderDash';

      return 'CertsActionBtnSelectedBorderSolid';
    };

    // const getAlertSubtitle = () => {
    //   const hasAccountType = verifyParentMap.some(
    //     (item) => item.type === 'account'
    //   );

    //   if (certKind === LINE_ITEM_TYPES.ACCOUNT)
    //     return 'To approve this Account, you must also approve the User:';

    //   if (certKind === LINE_ITEM_TYPES.ENTITLEMENT) {
    //     if (hasAccountType) {
    //       return 'To approve this Entitlement, you must also approve the User and any associated Accounts:';
    //     }

    //     return 'To approve this Entitlement, you must also approve the User:';
    //   }

    //   if (certKind === LINE_ITEM_TYPES.ROLE) {
    //     if (hasAccountType) {
    //       return 'To approve this Role, you must also approve the User and any associated Accounts:';
    //     }

    //     return 'To approve this Role, you must also approve the User:';
    //   }

    //   return 'Error in subtitle handler';
    // };

    // const handleAlertModalSubmit = (commentPopperValue) => {
    //   const isModalDrawerVersion = true;

    //   if (isCommentRequired && !isSelected)
    //     onClick(commentPopperValue, isModalDrawerVersion);
    //   if (!isCommentRequired && !isSelected) onClick();
    // };

    useEffect(() => {
      // Handling Error Banners on top of the Modals/Popovers based on Success or Error Action
      if (isActionSuccess) {
        if (kind === TERMINATE) {
          setIsCheckTerminateClick(false);
          setIsTerminateError(false);
        }

        if (kind === EXTEND_END_DATE) {
          setIsExtendEndDateClick(false);
          setIsExtendEndDateError(false);
        }

        if (kind === REJECT) {
          setIsRejectPopoverOpen(false);
          setIsRejectError(false);
        }

        if (kind === CONDITIONAL_CERTIFIY) {
          setIsConditionalCertifyClick(false);
          setIsConditionalCertifyError(false);
        }

        if (kind === APPROVE) {
          setIsSaviApprovePopoverOpen(false);
        }
      }

      if (isActionError) {
        if (kind === TERMINATE) {
          setIsTerminateError(true);
        }

        if (kind === EXTEND_END_DATE) {
          setIsExtendEndDateError(true);
        }

        if (kind === REJECT) {
          setIsRejectError(true);
        }

        if (kind === CONDITIONAL_CERTIFIY) {
          setIsConditionalCertifyError(true);
        }
      }
    }, [isActionSuccess, isActionError]);

    const handleConsultWarnModal = () => {
      setIsConsultWarnModalOpen((prevState) => !prevState);
    };

    const handleConsultModal = () => {
      const details = {
        // commented this code due to include associated check box is not for Reassign - Remove after beta Release
        // isIncludeAssociatedEnt: nodeData?.showConsultOrReassignAssociatedFlag
        //   ? nodeData?.showConsultOrReassignAssociatedFlag
        //   : false,
        isIncludeAssociatedEnt: false,
        isNoteRequired: true,
        noteText: l('Dependent parent will be approved'),
        isIncludeAsscLabel: '',
      };

      if (isEOCampaign) {
        switch (nodeData?.ROW_TYPE?.toString()) {
          case EO_ENT_CERT_ENTITLEMENT_VALUEKEY:
            details.lineItemKind = LINE_ITEM_TYPES.ENTITLEMENT;
            break;
          case EO_ACC_CERT_ENTITLEMENT_VALUEKEY:
            details.lineItemKind = LINE_ITEM_TYPES.ACCOUNT;
            break;
          case EO_CHILD_CERT_ENTITLEMENT_VALUEKEY:
            details.lineItemKind = LINE_ITEM_TYPES.CHILD_ENTITLEMENT;
            break;
          default:
            break;
        }
      } else {
        switch (nodeData?.CERT_ENTITLEMENT_VALUEKEY) {
          case CERT_ENTITLEMENT_VALUEKEY.USER:
            // details.isIncludeAsscLabel =
            //   'Include Associated Items (Accounts and Entitlements)';
            details.lineItemKind = LINE_ITEM_TYPES.USER;
            details.noteText = `Access items associated with this ${LINE_ITEM_TYPES.USER.toLowerCase()} are included.`;
            break;
          case CERT_ENTITLEMENT_VALUEKEY.ACCOUNT:
            if (
              CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT &&
              reassignConfigMap.enableEmploymentVerificationOnly
            ) {
              details.lineItemKind = LINE_ITEM_TYPES.ACCOUNT;
              break;
            } else {
              details.isIncludeAssociatedEnt = true; // enable checkbox only for account lineitem
              details.isIncludeAsscLabel = l('Include Associated Items'); // (Entitlements and Application Roles) not required - remove after beta release
            }

            details.lineItemKind = LINE_ITEM_TYPES.ACCOUNT;
            break;
          case CERT_ENTITLEMENT_VALUEKEY.ROLE:
            details.lineItemKind = LINE_ITEM_TYPES.ROLE;
            break;
          case CERT_ENTITLEMENT_VALUEKEY.ENTITLEMENT:
          default:
            details.isIncludeAsscLabel = l('Include Associated Items');
            if (CERT_ENTITLEMENT_VALUEKEY.ENTITLEMENT >= 0)
              details.lineItemKind = LINE_ITEM_TYPES.ENTITLEMENT;
            details.isIncludeAsscLabel = l(
              'Include Associated Items (Account, Entitlements and Application Roles)'
            );
            break;
        }
      }

      setIsConsultModalOpen(true);
      setconsultModalDetails(details);
    };

    const handleReassignModal = () => {
      const details = {
        // commented this code due to include associated check box is not for Reassign - Remove after beta Release
        // isIncludeAssociatedEnt: nodeData?.showConsultOrReassignAssociatedFlag
        //   ? nodeData?.showConsultOrReassignAssociatedFlag
        //   : false,
        isIncludeAssociatedEnt: false,
        isNoteRequired: true,
        noteText: l('Dependent parent will be approved'),
        isIncludeAsscLabel: '',
        actionKind: kind,
      };

      if (isEOCampaign) {
        // for only EO campaign
        switch (nodeData?.ROW_TYPE?.toString()) {
          case EO_ENT_CERT_ENTITLEMENT_VALUEKEY: {
            details.isNoteRequired = true;
            details.lineItemKind = LINE_ITEM_TYPES.ENTITLEMENT;
            details.noteText = `Access items associated with this ${LINE_ITEM_TYPES.ENTITLEMENT.toLowerCase()} are included.`;
            break;
          }

          case EO_ACC_CERT_ENTITLEMENT_VALUEKEY:
            details.lineItemKind = LINE_ITEM_TYPES.ACCOUNT;
            break;
          case EO_CHILD_CERT_ENTITLEMENT_VALUEKEY:
            details.lineItemKind = LINE_ITEM_TYPES.CHILD_ENTITLEMENT;
            break;
          default:
            break;
        }
      } else {
        switch (nodeData?.CERT_ENTITLEMENT_VALUEKEY) {
          case CERT_ENTITLEMENT_VALUEKEY.USER:
            details.isNoteRequired = true;
            details.lineItemKind = LINE_ITEM_TYPES.USER;
            details.noteText = `Access items associated with this ${LINE_ITEM_TYPES.USER.toLowerCase()} are included.`;
            break;

          case CERT_ENTITLEMENT_VALUEKEY.ACCOUNT: {
            details.isIncludeAsscLabel = l(
              'Include Associated Items (Entitlements and Application Roles)'
            );
            details.lineItemKind = LINE_ITEM_TYPES.ACCOUNT;
            break;
          }

          case CERT_ENTITLEMENT_VALUEKEY.ROLE:
            details.lineItemKind = LINE_ITEM_TYPES.ROLE;
            // commented this code due to include associated check box is not for Reassign - Remove after beta Release
            // enable the comment code after confirmation from the product team
            // if(nodeData?.ENTITLEMENTTYPE === 'APPLICATION ROLE'){
            //   details.isIncludeAssociatedEnt = true; // role lineitem application role
            //   details.isIncludeAsscLabel = 'Include Associated items (Account, Entitlements and Application Roles)';
            // }else{
            // details.isIncludeAssociatedEnt = false; // no need checkbox for role lineitem (Enterprise and fire fighter role)
            // }

            break;
          case CERT_ENTITLEMENT_VALUEKEY.ENTITLEMENT:
          default:
            details.isIncludeAsscLabel = l('Include Associated Items');
            if (CERT_ENTITLEMENT_VALUEKEY.ENTITLEMENT >= 0)
              details.lineItemKind = LINE_ITEM_TYPES.ENTITLEMENT;
            details.isIncludeAsscLabel = l(
              'Include Associated Items (Account, Entitlements and Application Roles)'
            );
            break;
        }
      }

      setIsReassignModalOpen(true);
      setReassignModalDetails(details);
    };

    const handleActionClick = (proceedActionForConsultedRecord = true) => {
      setIsActionDrawerOutsideClick(false);
      setIsSidePanelCommentDrawerOpen(false);
      setDrawerActionKind(null);

      // condition to show consulted record popover to take other actions
      const showConsultedPopover =
        isConsulted &&
        !consulteeLoggedIn &&
        !isConsultPopoverShown &&
        kind !== CONSULT &&
        kind !== EXTEND_END_DATE &&
        !isSaviResponded &&
        !isSidePanelVisible &&
        activeTab !== TAB_STATUS_DONE;

      // condition to show  popover to show details of action performed by non primary certifier
      const showActionedPopover =
        !actionTakenByPrimaryCertifier &&
        !isSaviResponded &&
        // isConsulted && // no need to check consulted record - remove once confirmation comes
        isSelected &&
        kind !== CONSULT;

      if (showConsultedPopover || showActionedPopover)
        setIsCommentPopoverOpen(true);

      // Start - don't execute handle action click when actioin taken by non primary certifier (Admin or consultee or secondary certifier) excluding consult
      if (
        !actionTakenByPrimaryCertifier &&
        kind !== CONSULT &&
        kind !== EXTEND_END_DATE &&
        isSelected &&
        !isSaviResponded
      ) {
        if (isFooterGroup) {
          setDrawerActionKind(kind);
          setIsSidePanelCommentDrawerOpen(true);
          setDrawerActionSelected(isSelected);
        }

        return;
      }
      // End - don't execute handle action click when action taken by non primary certifier (Admin or consultee or secondary certifier) excluding consult

      // Start Consult Record validation
      if (
        kind !== CONSULT &&
        kind !== EXTEND_END_DATE &&
        isConsulted &&
        !isSaviResponded &&
        proceedActionForConsultedRecord &&
        activeTab !== TAB_STATUS_DONE
      ) {
        if (!actionTakenByPrimaryCertifier && isSelected) {
          return;
        }

        if (isConsultPopoverShown && !isConsultWarnShown) {
          handleConsultWarnModal();

          return;
        }

        // dont't allow any actions
        if (!isConsultPopoverShown && !consulteeLoggedIn) {
          setDrawerActionKind(kind);
          setIsSidePanelCommentDrawerOpen(true);
          setDrawerActionSelected(isSelected);

          return;
        }
      }
      // End Consult Record validation

      if (kind === APPROVE && !isSelected) {
        if (isCommentRequired) {
          if (isDetailsGroup) setIsCommentPopoverOpen(true);

          if (isFooterGroup) {
            setDrawerActionKind(APPROVE);
            setIsSidePanelCommentDrawerOpen(true);
          }

          return;
        }

        onClick();

        return;
        // TODO: Commented out at Umeshes request, add or remove based on user feedback
        // setIsProceedStatus(false);

        // const getNameByType = () => {
        //   if (getTypeActEntRole === CERT_TILE_TYPE.ACCOUNT)
        //     return nodeData?.Account_DISPLAYNAME;

        //   if (getTypeActEntRole === CERT_TILE_TYPE.ROLE)
        //     return nodeData?.Role_DISPLAYNAME;

        //   if (getTypeActEntRole === CERT_TILE_TYPE.ENTITLEMENT)
        //     return nodeData?.Ent_DISPLAYNAME;

        //   return null;
        // };

        // const currentChildObject = {
        //   type: getTypeActEntRole.toLowerCase(),
        //   childname: getNameByType(),
        // };

        // const verifyPayload = {
        //   certKey,
        //   userKey,
        //   entValueKey,
        //   accountKey,
        //   roleKey,
        // };

        // getVarifyParentItemStatus(dispatch, verifyPayload, currentChildObject)
        //   .then((response) => {
        //     if (response.proceed) {
        //       setIsActionAlertModalOpen(false);
        //       setIsProceedStatus(true);

        //       if (isCommentRequired) {
        //         if (isDetailsGroup) setIsCommentPopoverOpen(true);

        //         if (isFooterGroup) {
        //           setDrawerActionKind(APPROVE);
        //           setIsSidePanelCommentDrawerOpen(true);
        //         }

        //         return;
        //       }

        //       onClick();

        //       return;
        //     }

        //     setIsActionAlertModalOpen(true);
        //     if (isDetailsGroup) setIsCommentPopoverOpen(false);
        //     if (isFooterGroup) setIsSidePanelCommentDrawerOpen(false);
        //   })
        //   .catch((error) => {
        //     Logger.log('Error handling API call:', error.message);
        //     setIsActionAlertModalOpen(false);
        //     setActionPopoverOpenId(false);
        //   });
        // TODO END: Commented out at Umeshes request, add or remove based on user feedback
      }

      if (kind === WORKS_FOR_ME) {
        if (isCommentRequired && !isSelected) {
          if (isDetailsGroup) setIsCommentPopoverOpen(true);

          if (isFooterGroup) {
            setDrawerActionKind(WORKS_FOR_ME);
            setIsSidePanelCommentDrawerOpen(true);
            setDrawerActionSelected(isSelected);
          }

          return;
        }

        onClick();

        return;
      }

      if (kind === BELONGS_TO_ME) {
        if (isCommentRequired && !isSelected) {
          if (isDetailsGroup) setIsCommentPopoverOpen(true);

          if (isFooterGroup) {
            setDrawerActionKind(BELONGS_TO_ME);
            setIsSidePanelCommentDrawerOpen(true);
            setDrawerActionSelected(isSelected);
          }

          return;
        }

        onClick();

        return;
      }

      // Set to open popover if required instead (handled isOpen set in CertsActionPopover)
      if (
        isCommentRequired &&
        !isSelected &&
        isDetailsGroup &&
        kind !== CONSULT &&
        kind !== REASSIGN &&
        kind !== DOES_NOT_WORK_FOR_ME &&
        kind !== DOES_NOT_BELONG_TO_ME
      )
        return;

      // Handling Terminate Modal Ui events
      if (kind === TERMINATE) {
        if (!isSelected) {
          setIsCheckTerminateClick(true);
          setIsTerminateError(false);

          return;
        }

        setIsCheckTerminateClick(false);
        onClick();

        return;
      }

      // Handling Extend End Date Modal Ui events
      if (kind === EXTEND_END_DATE) {
        setIsExtendEndDateClick(true);
        setIsExtendEndDateError(false);

        return;
      }

      // Handling Reject Popover Ui events
      if (kind === REJECT) {
        if (!isSelected && !isSidePanelVisible && isCommentRequired) {
          if (getFirstTimeReject || isCommentRequired) {
            setIsRejectPopoverOpen(true);
            setIsRejectError(false);

            return;
          }
        }

        if (isSaviResponded && isSelected) {
          setIsSaviRejectPopoverOpen(true);

          if (isSidePanelCommentDrawerOpen) {
            setIsSidePanelCommentDrawerOpen(false);
          }

          return;
        }

        setIsRejectPopoverOpen(false);
        onClick();

        return;
      }

      // Handling Reject Popover Ui events
      if (kind === DECOMMISSION) {
        if (!isSelected && !isSidePanelVisible && isCommentRequired) {
          if (isCommentRequired) {
            setIsDecommissionPopoverOpen(true);

            return;
          }
        }

        if (isSaviResponded && isSelected) {
          setIsSaviRejectPopoverOpen(true);

          if (isSidePanelCommentDrawerOpen) {
            setIsSidePanelCommentDrawerOpen(false);
          }

          return;
        }

        if (isCommentRequired && !isSelected) {
          if (isDetailsGroup) setIsCommentPopoverOpen(true);

          if (isFooterGroup) {
            setDrawerActionKind(ACTION_BUTTON_KINDS.DECOMMISSION);
            setIsSidePanelCommentDrawerOpen(true);
            setDrawerActionSelected(isSelected);
          }

          return;
        }

        setIsDecommissionPopoverOpen(false);
        onClick();

        return;
      }

      // Handling Conditional Certify Modal Ui events
      if (kind === CONDITIONAL_CERTIFIY) {
        if (!isSelected) {
          setIsConditionalCertifyClick(true);
          setIsConditionalCertifyError(false);

          return;
        }

        setIsConditionalCertifyClick(false);
        onClick();

        return;
      }

      if (kind === DOES_NOT_WORK_FOR_ME) {
        if (!isSelected) {
          // Mobile Responsiveness mode conditions
          if (
            isMobileXlargeWidth &&
            !isPopoverUserReassignMsgShown &&
            enableReassignPopover(reassignConfigMap)
          ) {
            if (!proceedActionForConsultedRecord) {
              handleReassignModal();
            } else {
              setIsCommentPopoverOpen(true);
            }

            return;
          }

          // sidepanel reassign message
          if (isFooterGroup && !isMobileXlargeWidth) {
            if (isReassignUserOrParent && !isPopoverUserReassignMsgShown) {
              setDrawerActionKind(DOES_NOT_WORK_FOR_ME);
              setIsSidePanelCommentDrawerOpen(true);
              setDrawerActionSelected(isSelected);

              return;
            }
          }

          if (consulteeLoggedIn) {
            if (
              enableReassignPopover(reassignConfigMap) &&
              isPopoverUserReassignMsgShown
            ) {
              handleReassignModal();

              return;
            }

            if (
              !isCommentRequired &&
              enableReassignPopover(reassignConfigMap) &&
              isPopoverUserReassignMsgShown
            ) {
              onClick();

              return;
            }
          }

          if (
            !isCommentRequired &&
            !enableReassignPopover(reassignConfigMap) &&
            isPopoverUserReassignMsgShown
          ) {
            onClick();

            return;
          }

          if (
            isCommentRequired &&
            !enableReassignPopover(reassignConfigMap) &&
            isPopoverUserReassignMsgShown
          ) {
            handleReassignModal();

            return;
          }

          if (
            enableReassignPopover(reassignConfigMap) &&
            (isPopoverUserReassignMsgShown || !proceedActionForConsultedRecord)
          ) {
            handleReassignModal();

            return;
          }

          if (!isPopoverUserReassignMsgShown) {
            return; // don't open any modal for reassign user until first popover accepted by user
          }
        }

        onClick();

        return;
      }

      // AO and SA Parent reassign
      if (kind === DOES_NOT_BELONG_TO_ME || isReassignUserOrParent) {
        if (!isSelected) {
          // Mobile Responsiveness mode conditions
          if (
            isMobileXlargeWidth &&
            !isPopoverUserReassignMsgShown &&
            enableReassignPopover(reassignConfigMap)
          ) {
            if (!proceedActionForConsultedRecord) {
              handleReassignModal();
            } else {
              setIsCommentPopoverOpen(true);
            }

            return;
          }

          // sidepanel reassign message
          if (isFooterGroup && !isMobileXlargeWidth) {
            if (isReassignUserOrParent && !isPopoverUserReassignMsgShown) {
              setDrawerActionKind(kind);
              setIsSidePanelCommentDrawerOpen(true);
              setDrawerActionSelected(isSelected);

              return;
            }
          }

          // Consultee login
          if (consulteeLoggedIn) {
            if (!isCommentRequired && isPopoverUserReassignMsgShown) {
              onClick();

              return;
            }
          }

          if (
            !isCommentRequired &&
            !enableReassignPopover(reassignConfigMap) &&
            isPopoverUserReassignMsgShown
          ) {
            onClick();

            return;
          }

          if (
            isCommentRequired &&
            !enableReassignPopover(reassignConfigMap) &&
            isPopoverUserReassignMsgShown
          ) {
            handleReassignModal();

            return;
          }

          if (
            (isPopoverUserReassignMsgShown ||
              !proceedActionForConsultedRecord) &&
            enableReassignPopover(reassignConfigMap)
          ) {
            handleReassignModal();

            return;
          }

          if (!isPopoverUserReassignMsgShown) {
            return; // don't open any modal for reassign user until first popover accepted by user
          }
        }

        onClick();

        return;
      }

      if (kind === REASSIGN) {
        if (!isSelected) {
          handleReassignModal();

          return;
        }

        onClick();

        return;
      }

      // don't open any modal for reassign user until first popover accepted by user
      if (kind === DOES_NOT_WORK_FOR_ME && !isPopoverUserReassignMsgShown)
        return;

      if (isConsult) {
        if (isConsulted && isSelected && activeTab !== TAB_STATUS_DONE) {
          // Start -- only when record is consulted
          handleConsultWarnModal();

          return;
        }

        if (!isSelected || activeTab === TAB_STATUS_DONE) {
          handleConsultModal();

          return;
        }

        onClick();

        return;
      }

      if (kind === APPROVE) {
        if (isSaviResponded && isSelected) {
          setIsSaviApprovePopoverOpen(true);

          if (isSidePanelCommentDrawerOpen) {
            setIsSidePanelCommentDrawerOpen(false);
          }

          return;
        }
      }

      onClick();
    };

    const handleCommentSubmit = (commentPopperValue) => {
      if (isCommentRequired && !isSelected) onClick(commentPopperValue);
    };

    const handleReassignSubmit = (reassignPayload) => {
      // set first time notify message
      // handle popover and modal api call
      onClick(reassignPayload);
    };

    const handleConsultSubmit = (consultPayload) => {
      onClick(consultPayload);
    };

    const handleConsultPopover = (checkBoxSelected) => {
      if (checkBoxSelected) {
        localStorage.setItem(
          CHECKBOX_CONSTANTS.CONSULT_POPOVER,
          !checkBoxSelected
        );
      }

      setIsConsultPopoverShown(true);
      setIsCommentPopoverOpen(false);

      if (!isConsultWarnShown) {
        // open warn modal
        setIsConsultWarnModalOpen(true);
      } else {
        // reassign user forcefully setting flag to true
        if (isReassignUserOrParent) {
          setReassignUserPopoverMsg();
        }

        handleActionClick(false); // proceed consult action
      }
    };

    // using handler in side panel comment drawer
    const handleReassignUserPopover = () => {
      setReassignUserPopoverMsg();

      if (
        consulteeLoggedIn &&
        !isCommentRequired &&
        !enableReassignPopover(reassignConfigMap)
      ) {
        onClick();
      } else if (
        enableReassignPopover(reassignConfigMap) ||
        isCommentRequired
      ) {
        handleReassignModal();
      } else {
        onClick();
      }
    };

    useImperativeHandle(ref, () => ({
      childFunction: handleConsultPopover,
      getIsConsultPopoverShown: () => isConsultPopoverShown,
      handleReassignUserPopover,
    }));

    const ActionButtonTrigger = (
      <>
        <ButtonCore
          id={id}
          ariaLabel={kind}
          dataTheme={dataTheme}
          tabIndex={tabIndex}
          onClick={handleActionClick}
          isDisabled={isDisabled}
          className={actionButtonClasses}
          dataTestId={dataTestId}>
          <Box className='CertsActionButton-inner'>
            <Icon kind={getIconKind()} />
            <span className='CertsActionButton-overlay' />
          </Box>
          {kind === CONSULT &&
            isSelected &&
            (nodeData?.CONSULTED_USERCOUNT > 1 ? (
              <Box className='CertsActionButton-consultIndicator'>
                <Box>
                  <Typography kind='body3-bold'>
                    {nodeData?.CONSULTED_USERCOUNT}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Icon
                kind='CheckCircleFilledSeparateColors'
                size='smallMedium'
                className='CertsActionButton-consultIcon'
              />
            ))}

          {isSelected && kind !== CONSULT && (
            <Icon
              className='CertsActionButton-borderIcon'
              kind={getBorderIcon()}
            />
          )}
          {isLoading && (
            <Lottie
              className='CertsActionButton-borderLoading'
              animationData={ActionButtonLoader}
            />
          )}
        </ButtonCore>
        {isMobileXlargeWidth && !isHidden && isFooterGroup && (
          <Box className='CertsTileActionsDrawer-item CertsTileActionsDrawer-sidepanelItem'>
            <Box className='MobileDrawerItem-primaryLabelWrapper'>
              <Typography
                kind='label'
                desktop='body4-bold-d'
                mobile='body4-bold-m'
                className={cx(
                  'MobileDrawerItem-label',
                  isSelected && 'MobileDrawerItem-label--selected',
                  isDisabled && 'MobileDrawerItem-label--isDisabled'
                )}>
                {getDrawerItemLabel()}
              </Typography>
            </Box>
          </Box>
        )}
        {kind === TERMINATE && checkTerminateClick && (
          <CertsTerminateModal
            isMobileXlargeWidth={isMobileXlargeWidth}
            autoRevoke={getViewApiConfigmap?.LOCKTASKEXPIRE === '1'}
            commentStatus={isCommentRequired}
            handleClick={onClick}
            handleCancel={() => {
              setIsCheckTerminateClick(false);
              setIsActionDrawerOutsideClick(true);
            }}
            isLoading={isLoading}
            isTerminateError={isTerminateError}
            setIsTerminateError={() => setIsTerminateError(false)}
            getDescriptionFlag={
              getViewApiConfigmap?.LOCKTASKEXPIRE === '1' ||
              getViewApiConfigmap?.LOCKTASKS === '1'
            }
          />
        )}
        {kind === EXTEND_END_DATE && isExtendEndDateClick && (
          <CertsChangeEndDate
            isMobileXlargeWidth={isMobileXlargeWidth}
            handleClick={onClick}
            handleCancel={() => {
              setIsExtendEndDateClick(false);
              setIsActionDrawerOutsideClick(true);
            }}
            getCurrentEndDate={getCurrentDate}
            isChangeEndDateError={isExtendEndDateError}
            setIsChangeEndDateError={() => setIsExtendEndDateError(false)}
            allowedChangeExtendDate={extendEndDateDaysLimit}
            isLoading={isLoading}
            certificationStartDate={new Date(certificationStartDate)}
            errorMessage={isActionErrorMessage}
            getDefaultEndDate={getDefaultDate}
            isCertsKindUser={
              nodeData?.CERT_ENTITLEMENT_VALUEKEY ===
              CERT_ENTITLEMENT_VALUEKEY.USER
            }
          />
        )}
        {kind === CONDITIONAL_CERTIFIY && isConditionalCertifyClick && (
          <CertsChangeEndDate
            isMobileXlargeWidth={isMobileXlargeWidth}
            handleClick={onClick}
            handleCancel={() => {
              setIsConditionalCertifyClick(false);
              setIsActionDrawerOutsideClick(true);
            }}
            getCurrentEndDate={getCurrentDate}
            isChangeEndDateError={getIsConditionalCertifyError}
            setIsChangeEndDateError={() => setIsConditionalCertifyError(false)}
            allowedChangeExtendDate={conditionalCertifyDaysLimit}
            isLoading={isLoading}
            certificationStartDate={new Date(certificationStartDate)}
            errorMessage={isActionErrorMessage}
            getDefaultEndDate={getDefaultDate}
          />
        )}

        {/* Reassign Modal and Confirmation modal */}

        {(kind === DOES_NOT_WORK_FOR_ME ||
          kind === REASSIGN ||
          kind === DOES_NOT_BELONG_TO_ME) &&
          isReassignModalOpen && (
            <CertsReassignModal
              isReassignModalOpen={isReassignModalOpen}
              setIsReassignModalOpen={() => setIsReassignModalOpen(false)}
              setIsActionDrawerOutsideClick={setIsActionDrawerOutsideClick}
              onSubmit={handleReassignSubmit}
              isCommentRequired={isCommentRequired}
              reassignModalDetails={reassignModalDetails}
              nodeData={nodeData}
            />
          )}

        {kind === CONSULT && isConsultModalOpen && (
          <CertsConsultModal
            isConsultModalOpen={isConsultModalOpen}
            setIsConsultModalOpen={() => {
              setIsConsultModalOpen(false);
              setIsActionDrawerOutsideClick(true);
            }}
            onSubmit={handleConsultSubmit}
            isCommentRequired={isCommentRequired}
            consultModalDetails={consultModalDetails}
            nodeData={nodeData}
          />
        )}
        {isConsultWarnModalOpen && (
          <CertsConsultWarnModal
            warnText={getWarnModalText(
              messages,
              localize,
              intl,
              kind,
              nodeData?.CONSULTED_USERNAME,
              nodeData?.CONSULTED_USERCOUNT
            )}
            btnText={getBtnText(messages, localize, intl, kind)}
            showCheckBox={kind !== CONSULT}
            title={getWarnModalTitle(
              messages,
              localize,
              intl,
              nodeData?.CONSULTED_USERCOUNT
            )}
            isConsultWarnModalOpen={isConsultWarnModalOpen}
            isActionLoading={isLoading}
            handleClose={() => {
              handleConsultWarnModal();
              setIsActionDrawerOutsideClick(true);
            }}
            errorMessage={isActionErrorMessage}
            handleWarnModal={
              kind === CONSULT
                ? () => {
                    handleConsultWarnModal();
                    setTimeout(
                      () => {
                        handleConsultModal();
                      },
                      isMobileXlargeWidth ? PERCENTAGE_100 : 0
                    );
                  }
                : (checkBoxSelected) => {
                    if (checkBoxSelected) {
                      localStorage.setItem(
                        CHECKBOX_CONSTANTS.CONSULT_WARN,
                        !checkBoxSelected
                      );
                    }

                    setIsConsultWarnShown(true);
                    handleConsultWarnModal();

                    // reassign user forcefully setting flag to true
                    if (isReassignUserOrParent) {
                      setReassignUserPopoverMsg();
                      setTimeout(() => {
                        handleActionClick(false); // proceed consult action
                      }, 200);

                      return;
                    }

                    handleActionClick(false); // proceed consult action
                  }
            }
            isActionSuccess={isActionSuccess}
          />
        )}
        {/* Approve Modal for child before parent */}
        {/* {kind === APPROVE && isActionAlertModalOpen && (
          <CertsActionAlertModal
            actionKind='approve'
            isCommentRequired={isCommentRequired}
            title='Approve All Items?'
            subtitle={getAlertSubtitle()}
            verifyParentMap={verifyParentMap}
            submitBtnText={
              verifyParentMap?.length > 0
                ? `Approve All (${verifyParentMap?.length})`
                : 'Approve All'
            }
            submitHandler={(commment) => handleAlertModalSubmit(commment)}
            isActionAlertModalOpen={isActionAlertModalOpen}
            setIsActionAlertModalOpen={setIsActionAlertModalOpen}
            setActionPopoverOpenId={setActionPopoverOpenId}
          />
        )} */}
      </>
    );

    const getActionButton = () => {
      if (isDrawerActions) {
        const getConsultIndicator = () =>
          nodeData?.CONSULTED_USERCOUNT > 1 ? (
            <Box className='CertsActionButton-consultIndicator'>
              <Box>
                <Typography kind='body3-bold'>
                  {nodeData?.CONSULTED_USERCOUNT}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Icon
              kind='CheckCircleFilledSeparateColors'
              size='smallMedium'
              className='CertsActionButton-consultIcon'
            />
          );

        return (
          <>
            <Box tag='div' className='CertsTileActionsDrawer-actionPlaceholder'>
              {ActionButtonTrigger}
            </Box>
            <MobileDrawerItem
              option={{
                label: getDrawerItemLabel(),
                value: id,
                prefixIcon: getIconKind(),
                onClick,
                isChecked: isSelected && kind !== CONSULT,
                isDisabled,
                secondaryLabel:
                  isConsulted && kind === CONSULT
                    ? tooltipProperties.text
                    : null,
              }}
              BadgeComp={
                isConsulted && kind === CONSULT ? getConsultIndicator() : null
              }
              onClick={handleActionClick}
              suffixIcon={
                isLoading ? (
                  <Loader
                    kind='dots'
                    format='inline'
                    color={isSelected ? 'inverse' : 'brand'}
                  />
                ) : null
              }
              className='CertsTileActionsDrawer-item'
            />
          </>
        );
      }

      return (
        <Tooltip
          className={actionButtonTooltipClasses}
          openDelay={mergedTooltipProperties.openDelay}
          text={mergedTooltipProperties.text}
          maxWidth={mergedTooltipProperties.maxWidth}
          placement={
            !isCommentPopoverOpen ||
            !isRejectPopoverOpen ||
            !isDecommissionPopoverOpen
              ? mergedTooltipProperties.placement
              : 'right'
          }
          offset={8}
          isDisabled={isHidden}
          trigger={ActionButtonTrigger}
        />
      );
    };

    // action not taken by primary certifier and is consulted
    if (
      !actionTakenByPrimaryCertifier &&
      !isSaviResponded &&
      // isConsulted && // no need to check consulted record - remove once confirmation comes
      isSelected &&
      kind !== CONSULT &&
      kind !== EXTEND_END_DATE &&
      !isSidePanelVisible
    ) {
      return (
        <CertsConsultedActionPopover
          isMobileXlargeWidth={isMobileXlargeWidth}
          isCommentPopoverOpen={isCommentPopoverOpen}
          setIsCommentPopoverOpen={setIsCommentPopoverOpen}
          placement={getPopoverPlacement()}
          trigger={getActionButton()}
          onClose={() => {
            if (isCommentPopoverOpen) setIsCommentPopoverOpen(false);
          }}
          resetAction={() => {
            setIsCommentPopoverOpen(false);
            onClick(); // to reset action
          }}
          certKey={certKey}
          nodeData={nodeData}
          globalDateFormat={globalDateFormat}
        />
      );
    }

    if (
      isConsulted &&
      !consulteeLoggedIn &&
      !isConsultPopoverShown &&
      kind !== CONSULT &&
      kind !== EXTEND_END_DATE &&
      !isSaviResponded &&
      !isSidePanelVisible &&
      activeTab !== TAB_STATUS_DONE // dont show consult popover in done tab
    ) {
      return (
        <CertsConsultPopover
          isMobileXlargeWidth={isMobileXlargeWidth}
          actionKind={kind}
          isCommentPopoverOpen={isCommentPopoverOpen}
          setIsCommentPopoverOpen={setIsCommentPopoverOpen}
          placement={getPopoverPlacement()}
          trigger={getActionButton()}
          onSubmit={(checkBoxSelected) => {
            setReassignUserPopoverMsg();
            handleConsultPopover(checkBoxSelected);
          }}
        />
      );
    }

    if (
      (isConsulted &&
        !consulteeLoggedIn &&
        isConsultPopoverShown &&
        isConsultWarnShown) ||
      !isConsulted ||
      consulteeLoggedIn ||
      (isConsulted && activeTab === TAB_STATUS_DONE)
    ) {
      if (isSaviRejectPopoverOpen) {
        return (
          <CertsSaviActionPopover
            kind={REJECT}
            handleClick={onClick}
            onClose={setIsSaviRejectPopoverOpen}
            isOpen={isSaviRejectPopoverOpen}
            trigger={getActionButton()}
            placement={getPopoverPlacement()}
            saviRespondedReason={saviRespondedReason}
            getTypeActEntRole={getTypeActEntRole}
            getCertIconKind={getCertIconKinds}
            getColKey={certTitleColValue}
          />
        );
      }

      if (isSaviApprovePopoverOpen) {
        return (
          <CertsSaviActionPopover
            kind={APPROVE}
            handleClick={onClick}
            onClose={setIsSaviApprovePopoverOpen}
            isOpen={isSaviApprovePopoverOpen}
            trigger={getActionButton()}
            placement={getPopoverPlacement()}
            saviRespondedReason={saviRespondedReason}
            getTypeActEntRole={getTypeActEntRole}
            getCertIconKind={getCertIconKinds}
            getColKey={certTitleColValue}
          />
        );
      }

      if (
        isConfirmManagerOrApproveTileAndCommentRequired &&
        !isSidePanelVisible
      ) {
        return (
          <CertsActionPopover
            actionKind={kind}
            isMobileXlargeWidth={isMobileXlargeWidth}
            isCommentPopoverOpen={
              kind === APPROVE ||
              kind === WORKS_FOR_ME ||
              kind === BELONGS_TO_ME
                ? isCommentPopoverOpen
                : false
            }
            setIsCommentPopoverOpen={
              isSelected ? () => {} : setIsCommentPopoverOpen
            }
            placement={getPopoverPlacement()}
            trigger={getActionButton()}
            onSubmit={handleCommentSubmit}
            getTypeActEntRole={getTypeActEntRole}
          />
        );
      }

      // enable the popover when reassign and show reassign , comments if not required  popover is false
      if (
        isReassignUserOrParent &&
        // isCommentRequired &&
        // enableReassignPopover(reassignConfigMap) &&
        !isPopoverUserReassignMsgShown &&
        !isReassignModalOpen &&
        (!isSidePanelVisible || isMobileXlargeWidth)
      ) {
        return (
          <CertsReassignPopover
            actionKind={kind}
            nodeData={nodeData}
            isCommentPopoverOpen={isCommentPopoverOpen}
            isMobileXlargeWidth={isMobileXlargeWidth}
            getTypeActEntRole={getTypeActEntRole}
            setIsCommentPopoverOpen={
              isSelected ? () => {} : setIsCommentPopoverOpen
            }
            placement={getPopoverPlacement()}
            trigger={getActionButton()}
            onSubmit={() => {
              if (isCommentPopoverOpen) setIsCommentPopoverOpen(false);

              setReassignUserPopoverMsg();

              if (consulteeLoggedIn && !isCommentRequired) {
                onClick();
              } else if (
                enableReassignPopover(reassignConfigMap) ||
                isCommentRequired
              ) {
                handleReassignModal();
              } else {
                onClick();
              }
            }}
          />
        );
      }

      /* Account, Entitlement and Role Reject Action Popover */
      if (
        isRejectPopoverOpen &&
        isCommentRequired &&
        !getIsRejectError &&
        !isSidePanelVisible
      )
        return (
          <CertsRejectAction
            isMobileXlargeWidth={isMobileXlargeWidth}
            autoRevoke={getViewApiConfigmap?.LOCKTASKEXPIRE === '1'}
            commentStatus={isCommentRequired}
            handleClick={onClick}
            isLoading={isLoading}
            getTypeActEntRole={getTypeActEntRole}
            placement={getPopoverPlacement()}
            trigger={getActionButton()}
            isRejectPopoverOpen={isRejectPopoverOpen}
            setIsRejectPopoverOpen={
              isSelected ? () => {} : setIsRejectPopoverOpen
            }
          />
        );
    }

    if (isDecommissionPopoverOpen && isCommentRequired && !isSidePanelVisible)
      return (
        <CertsDecommissionAction
          isMobileXlargeWidth={isMobileXlargeWidth}
          autoRevoke={getViewApiConfigmap?.LOCKTASKEXPIRE === '1'}
          commentStatus={isCommentRequired}
          handleClick={onClick}
          isLoading={isLoading}
          getTypeActEntRole={getTypeActEntRole}
          placement={getPopoverPlacement()}
          trigger={getActionButton()}
          isDecommissionPopoverOpen={isDecommissionPopoverOpen}
          setIsDecommissionPopoverOpen={
            isSelected ? () => {} : setIsDecommissionPopoverOpen
          }
        />
      );

    return <>{getActionButton()}</>;
  }
);

CertsActionButton.defaultProps = {
  id: null,
  setActionPopoverOpenId: () => {},
  setIsSidePanelCommentDrawerOpen: () => {},
  setDrawerActionSelected: () => {},
  tabIndex: 0,
  isDisabled: false,
  isSelected: false,
  isLoading: false,
  isDecisionMade: false,
  isCommentRequired: false,
  isToolTipDisabled: false,
  tooltipProperties: defaultTooltipProps,
  dataTheme: null,
  dataTestId: null,
  getViewApiConfigmap: null,
  extendEndDateDaysLimit: null,
  isActionSuccess: false,
  isActionError: false,
  getTypeActEntRole: null,
  isSidePanelVisible: null,
  reassignConfigMap: {
    defaultReassignee: '',
    defaultReassigneeName: '',
    enableIndividualCommentsForAccountEntitlementRoles: [],
    enableIndividualCommentsForUsers: [],
    enableReassignStep2: '0',
    enableReassignUser: '0',
    showReassignPopup: '0',
  },
  getFirstTimeReject: null,
  certificationStartDate: null,
  isActionErrorMessage: null,
  conditionalCertifyDaysLimit: null,
  isPopoverUserReassignMsgShown: false,
  setReassignUserPopoverMsg: () => {},
  setDrawerActionKind: () => {},
  isSaviResponded: false,
  saviRespondedReason: [],
  consulteeLoggedIn: false,
  isScrolling: false,
  isActionGroupExpanded: false,
  activeTab: TAB_STATUS_TODO,
  filteredNodeData: {},
  isActionDrawerOutsideClick: true,
  setIsActionDrawerOutsideClick: () => {},
};

CertsActionButton.propTypes = {
  id: PropTypes.string,
  setActionPopoverOpenId: PropTypes.func,
  setIsSidePanelCommentDrawerOpen: PropTypes.func,
  setDrawerActionSelected: PropTypes.func,
  kind: PropTypes.oneOf(Object.values(ACTION_BUTTON_KINDS)).isRequired,
  groupKind: PropTypes.oneOf(Object.values(ACTION_BUTTON_GROUP_KINDS))
    .isRequired,
  tabIndex: PropTypes.number,
  isDisabled: PropTypes.bool,
  isSelected: PropTypes.bool,
  isLoading: PropTypes.bool,
  isDecisionMade: PropTypes.bool,
  isDrawerActions: PropTypes.bool.isRequired,
  isCommentRequired: PropTypes.bool,
  isHidden: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  isToolTipDisabled: PropTypes.bool,
  tooltipProperties: PropTypes.shape({
    text: PropTypes.string,
    openDelay: PropTypes.number,
    maxWidth: PropTypes.string,
    placement: PropTypes.string,
  }),
  dataTheme: PropTypes.oneOf(Object.values(SYSTEM_DATA_THEMES)),
  dataTestId: PropTypes.string,
  certKey: PropTypes.string.isRequired,
  nodeData: PropTypes.shape(NODE_DATA_MODEL).isRequired,
  // TODO: Commented out at Umeshes request, add or remove based on user feedback
  // verifyParentMap: PropTypes.arrayOf(VERIFY_PARENT_MAP_MODEL),
  // eslint-disable-next-line react/forbid-prop-types
  getViewApiConfigmap: PropTypes.any,
  extendEndDateDaysLimit: PropTypes.string,
  isActionSuccess: PropTypes.bool,
  isActionError: PropTypes.bool,
  getTypeActEntRole: PropTypes.string,
  isSidePanelVisible: PropTypes.bool,
  getFirstTimeReject: PropTypes.bool,
  certificationStartDate: PropTypes.string,
  isActionErrorMessage: PropTypes.string,
  conditionalCertifyDaysLimit: PropTypes.string,
  reassignConfigMap: PropTypes.shape(REASSIGN_CONFIG_MAP),
  isPopoverUserReassignMsgShown: PropTypes.bool,
  setReassignUserPopoverMsg: PropTypes.func,
  setDrawerActionKind: PropTypes.func,
  isSaviResponded: PropTypes.bool,
  saviRespondedReason: PropTypes.arrayOf(PropTypes.string),
  isSidePanelCommentDrawerOpen: PropTypes.bool.isRequired,
  consulteeLoggedIn: PropTypes.bool,
  actionTakenByPrimaryCertifier: PropTypes.bool.isRequired,
  isScrolling: PropTypes.bool,
  globalDateFormat: PropTypes.string.isRequired,
  isActionGroupExpanded: PropTypes.bool,
  activeTab: PropTypes.string,
  filteredNodeData: PropTypes.shape({}),
  isActionDrawerOutsideClick: PropTypes.bool,
  setIsActionDrawerOutsideClick: PropTypes.func,
};

export default CertsActionButton;
